import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
// Components
import Hero from './Hero';
import Toolbar from './Toolbar';
import MovieGrid, { LetterDivider } from './MovieGrid';
import MovieCard from './MovieCard';
import SkeletonCards from './Skeleton';
import StatusMessage from './StatusMessage';
import Button from './Button';
// Hooks
import { useHomeFetch } from '../hooks/useHomeFetch';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
// Helpers
import { indexLetter, sortTitle } from '../helpers';

const Section = styled.section`
  max-width: var(--maxWidth);
  margin: 0 auto;
  padding: 64px var(--gutter) 0;
`;

const More = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 56px;

  small {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--muted);
  }
`;

const readSort = () => {
  try {
    return sessionStorage.getItem('azSort') || 'popular';
  } catch (error) {
    return 'popular';
  }
};

const byTitle = (a, b) =>
  sortTitle(a.title).localeCompare(sortTitle(b.title), 'en', {
    sensitivity: 'base',
    numeric: true
  });

const Home = () => {
  const [params] = useSearchParams();
  const searchTerm = (params.get('q') || '').trim();
  const { state, loading, error, loadMore, retry } = useHomeFetch(searchTerm);
  const [sort, setSortState] = useState(readSort);

  useDocumentTitle(searchTerm ? `“${searchTerm}”` : '');

  const setSort = value => {
    setSortState(value);
    try {
      sessionStorage.setItem('azSort', value);
    } catch (e) {
      // ignore
    }
  };

  const showHero = !searchTerm && state.results.length > 0;
  const featured = showHero ? state.results[0] : null;
  const upNext = showHero ? state.results.slice(1, 5) : [];

  const sorted = useMemo(() => {
    const list = [...state.results];
    switch (sort) {
      case 'az':
        return list.sort(byTitle);
      case 'za':
        return list.sort((a, b) => byTitle(b, a));
      case 'rating':
        return list.sort(
          (a, b) => (b.vote_average || 0) - (a.vote_average || 0)
        );
      case 'newest':
        return list.sort((a, b) =>
          (b.release_date || '').localeCompare(a.release_date || '')
        );
      default:
        return list;
    }
  }, [state.results, sort]);

  const alphabetical = sort === 'az' || sort === 'za';

  // Counts per letter for the A–Z dividers and jump rail
  const letterCounts = useMemo(() => {
    const counts = new Map();
    if (!alphabetical) return counts;
    sorted.forEach(movie => {
      const letter = indexLetter(movie.title);
      counts.set(letter, (counts.get(letter) || 0) + 1);
    });
    return counts;
  }, [sorted, alphabetical]);

  const gridItems = [];
  let lastLetter = null;
  sorted.forEach((movie, i) => {
    if (alphabetical) {
      const letter = indexLetter(movie.title);
      if (letter !== lastLetter) {
        gridItems.push(
          <LetterDivider
            key={`l-${letter}`}
            letter={letter}
            count={letterCounts.get(letter)}
          />
        );
        lastLetter = letter;
      }
    }
    gridItems.push(<MovieCard key={movie.id} movie={movie} index={i} />);
  });

  const initialLoading = loading && state.results.length === 0;
  const noResults = !loading && !error && searchTerm && state.results.length === 0;
  const hasMore = state.page < state.total_pages;

  return (
    <>
      {showHero && <Hero featured={featured} upNext={upNext} />}

      <Section aria-live='polite'>
        <Toolbar
          eyebrow={searchTerm ? 'Search results' : 'The index'}
          title={
            searchTerm ? (
              <>
                <em>for</em> “{searchTerm}”
              </>
            ) : (
              'Popular movies'
            )
          }
          count={
            state.total_results > 0 && (
              <p>
                {state.total_results.toLocaleString('en-US')} titles ·{' '}
                {state.results.length} loaded
                {sort !== 'popular' && ' · sorting loaded titles'}
              </p>
            )
          }
          sort={sort}
          setSort={setSort}
          letters={alphabetical && sorted.length ? new Set(letterCounts.keys()) : null}
        />

        {error && state.results.length === 0 ? (
          <StatusMessage
            glyph='?!'
            title='We lost the signal.'
            action={<Button text='Try again' callback={retry} />}
          >
            The movie database didn’t respond. Check your connection and give it
            another go.
          </StatusMessage>
        ) : noResults ? (
          <StatusMessage glyph='Ø' title='Nothing on the reel.'>
            No movies match “{searchTerm}”. Try a different spelling or a
            shorter title.
          </StatusMessage>
        ) : (
          <MovieGrid>
            {gridItems}
            {loading && <SkeletonCards count={initialLoading ? 15 : 5} />}
          </MovieGrid>
        )}

        {state.results.length > 0 && (
          <More>
            {error ? (
              <Button text='Couldn’t load more — retry' callback={retry} />
            ) : hasMore ? (
              <Button
                text={loading ? 'Loading…' : 'Load more movies'}
                callback={loadMore}
                disabled={loading}
              />
            ) : (
              <small>That’s everything — from A to Z.</small>
            )}
            {hasMore && (
              <small>
                Page {state.page} of {state.total_pages.toLocaleString('en-US')}
              </small>
            )}
          </More>
        )}
      </Section>
    </>
  );
};

export default Home;
